import {
  add,
  addHours,
  addMinutes,
  eachDayOfInterval,
  format,
  isAfter,
  parse,
  startOfMinute,
} from 'date-fns';

import { TSchedulerDate, TSchedulerTimeList } from 'components/modal-schedule-date-time/types';
import { IOperatingHours } from 'generated/rbi-graphql';
import { getDateInTimezone } from 'utils/dateTime';
import { getRestaurantCateringHours } from 'utils/restaurant';

import { CateringPickupScheduleDaysLimit } from './constants';

export const formatDate = (date: Date) => {
  return format(date, 'MMMM dd, yyyy');
};

export const parseTime = (time: string, referenceDate: Date): Date => {
  return parse(time, 'HH:mm:ss', referenceDate);
};

export const getDatesForCateringPickup = (startDate: Date) => {
  const dateAfter24Hours = addHours(startDate, 24);
  const dateAfterNumberOfDays = add(dateAfter24Hours, {
    days: CateringPickupScheduleDaysLimit - 1,
  });

  const dates = eachDayOfInterval({ start: dateAfter24Hours, end: dateAfterNumberOfDays });
  return dates;
};

export const getTimeIntervals = (startDate: Date, endDate: Date): string[] => {
  const intervals: string[] = [];

  let currentDateTime = startDate;
  while (currentDateTime < endDate) {
    intervals.push(format(currentDateTime, 'hh:mm a'));
    currentDateTime = addMinutes(currentDateTime, 30);
  }
  return intervals;
};

export const getFirstAvailableDateTime = (date: Date): Date => {
  const startingTimeAfter24Hours = add(date, { days: 1 });
  const roundedTime = startOfMinute(startingTimeAfter24Hours);

  // Round minutes to the nearest 30 minutes
  const minutes = roundedTime.getMinutes();
  const roundedMinutes = minutes < 30 ? 30 : 60;
  roundedTime.setMinutes(roundedMinutes);

  return roundedTime;
};

export const getTimeListForDate = ({
  selectedDate,
  cateringHours,
  dineInHours,
  storeTimezoneName,
}: {
  selectedDate: Date;
  cateringHours?: IOperatingHours | null;
  dineInHours?: IOperatingHours | null;
  storeTimezoneName?: string | null;
}) => {
  if (!cateringHours || !dineInHours) {
    return [];
  }
  const currentDateInStoreTimezone = getDateInTimezone({ timezone: storeTimezoneName });

  const firstAvailableDateTime = getFirstAvailableDateTime(currentDateInStoreTimezone);
  const [openHours, closeHours] = getRestaurantCateringHours(
    cateringHours,
    dineInHours,
    selectedDate
  );
  const parsedOpenDateTime = parseTime(openHours, selectedDate);
  const parsedCloseDateTime = parseTime(closeHours, selectedDate);

  const adjustedOpenDateTime = isAfter(firstAvailableDateTime, parsedOpenDateTime)
    ? firstAvailableDateTime
    : parsedOpenDateTime;

  return getTimeIntervals(adjustedOpenDateTime, parsedCloseDateTime);
};

export const getCateringDateTimeForScheduler = ({
  cateringHours,
  dineInHours,
  storeTimezoneName,
}: {
  cateringHours?: IOperatingHours | null;
  dineInHours?: IOperatingHours | null;
  storeTimezoneName?: string | null;
}): Map<TSchedulerDate, TSchedulerTimeList> => {
  const currentDateInStoreTimezone = getDateInTimezone({ timezone: storeTimezoneName });

  const datesForPickup = getDatesForCateringPickup(currentDateInStoreTimezone);

  const dateTimeMap = datesForPickup.reduce((acc, currentDateForPickup) => {
    const timeListForDate = getTimeListForDate({
      selectedDate: currentDateForPickup,
      cateringHours,
      dineInHours,
      storeTimezoneName,
    });
    const dateItem = {
      value: formatDate(currentDateForPickup),
      disabled: !timeListForDate.length,
      description: timeListForDate.length ? 'available' : 'unavailable',
    };

    acc.set(dateItem, timeListForDate);
    return acc;
  }, new Map());

  return dateTimeMap;
};
